/**
 * FlybyCamera.ts — preview camera that flies along an authored polyline
 * (`TerrainFlybyLineObject`, slug `flyby_line`).
 *
 * The path is a list of (world X, world Z) points. The camera position is
 * the path point at the current arc-length parameter `s`, raised by
 * `heightM` above water level. The look-at target is the path point at
 * `s + lookAheadM` (clamped to the path end, or wrapped when looping).
 *
 * Camera orbital state (target / azimuth / elevation / distance) is
 * back-solved from the desired (eyePos, lookTarget) pair so that the
 * existing perspective + uniform upload path in {@link Camera} stays
 * untouched.
 */

import type { Camera } from "./Camera";

export interface FlybyPath {
  id: string;
  /** World-space polyline points (X, Z). */
  points: Array<[number, number]>;
  /** Camera altitude above water level, metres. */
  heightM: number;
  /** Traversal speed along the path, m/s. */
  speedMps: number;
  /** Restart from the first point after the last when true. */
  loop: boolean;
  /** Distance ahead along the path used as the look-at target, metres. */
  lookAheadM: number;
}

export class FlybyCamera {
  /** Current arc-length parameter along the active path, metres. */
  private s = 0;
  /** Per-segment cumulative arc length (length N for N+1 points). */
  private cumulative: number[] = [];
  private totalLength = 0;
  private active: FlybyPath | null = null;

  constructor(private camera: Camera) {}

  setPath(path: FlybyPath | null): void {
    this.active = path;
    this.s = 0;
    this.cumulative = [];
    this.totalLength = 0;
    if (path === null || path.points.length < 2) return;
    let acc = 0;
    this.cumulative.push(0);
    for (let i = 1; i < path.points.length; i++) {
      const a = path.points[i - 1]!;
      const b = path.points[i]!;
      acc += Math.hypot(b[0] - a[0], b[1] - a[1]);
      this.cumulative.push(acc);
    }
    this.totalLength = acc;
  }

  hasPath(): boolean {
    return this.active !== null && this.totalLength > 0;
  }

  /** Reset arc-length parameter to start of the path. */
  reset(): void {
    this.s = 0;
  }

  step(dt: number, waterLevel: number): void {
    const path = this.active;
    if (!path || this.totalLength <= 0) return;

    this.s += path.speedMps * dt;
    if (path.loop) {
      this.s =
        ((this.s % this.totalLength) + this.totalLength) % this.totalLength;
    } else if (this.s > this.totalLength) {
      this.s = this.totalLength;
    }

    const eyeXZ = this.sample(this.s);
    const targetParam = path.loop
      ? (((this.s + path.lookAheadM) % this.totalLength) + this.totalLength) %
        this.totalLength
      : Math.min(this.s + path.lookAheadM, this.totalLength);
    const lookXZ = this.sample(targetParam);

    const eyeX = eyeXZ[0];
    const eyeY = waterLevel + path.heightM;
    const eyeZ = eyeXZ[1];
    // Look slightly above the water at the look-ahead point so the camera
    // doesn't tilt down into the lake on long straight segments.
    const lookX = lookXZ[0];
    const lookY = waterLevel + Math.max(0, path.heightM * 0.25);
    const lookZ = lookXZ[1];

    // Back-solve orbital state so Camera.recompute() lands position = eye.
    // Camera convention: position = target + distance * (cos(el)*sin(az),
    //   sin(el), cos(el)*cos(az)).
    const dx = eyeX - lookX;
    const dy = eyeY - lookY;
    const dz = eyeZ - lookZ;
    const dist = Math.hypot(dx, dy, dz);
    const cam = this.camera;
    if (dist < 1e-3) {
      // Degenerate (look-ahead collapsed to the eye): keep prior framing.
      cam.target[0] = lookX;
      cam.target[1] = lookY;
      cam.target[2] = lookZ;
      cam.recompute();
      return;
    }
    const elevation = Math.asin(dy / dist);
    const azimuth = Math.atan2(dx, dz);

    cam.target[0] = lookX;
    cam.target[1] = lookY;
    cam.target[2] = lookZ;
    cam.azimuth = azimuth;
    cam.elevation = elevation;
    cam.distance = dist;
    cam.recompute();
  }

  /**
   * Sample the path at arc-length parameter `s` (metres). Returns the
   * interpolated (X, Z) point. Caller must guarantee `totalLength > 0`.
   */
  private sample(s: number): [number, number] {
    const path = this.active!;
    const cum = this.cumulative;
    const last = cum.length - 1;
    if (s <= 0) return [path.points[0]![0], path.points[0]![1]];
    if (s >= this.totalLength)
      return [path.points[last]![0], path.points[last]![1]];
    // Binary search for the segment whose cumulative range contains s.
    let lo = 0;
    let hi = last;
    while (lo + 1 < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid]! <= s) lo = mid;
      else hi = mid;
    }
    const segStart = cum[lo]!;
    const segEnd = cum[lo + 1]!;
    const segLen = segEnd - segStart;
    const t = segLen > 0 ? (s - segStart) / segLen : 0;
    const a = path.points[lo]!;
    const b = path.points[lo + 1]!;
    return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
  }
}

/**
 * Extract camera flyby paths from a baked `terrain_objects.json` list.
 * Defaults match `TerrainItemTypeSeeder` (`flyby_line` default_attributes):
 * `height_m: 25`, `speed_mps: 15.6`, `loop: true`, `look_ahead_m: 30`.
 */
export function collectFlybyPaths(worldObjects: unknown[]): FlybyPath[] {
  const paths: FlybyPath[] = [];
  for (const raw of worldObjects) {
    if (!raw || typeof raw !== "object") continue;
    const obj = raw as Record<string, unknown>;
    if (obj.type !== "flyby_line") continue;
    const geom = obj.geometry as
      | { type?: string; points?: Array<[number, number]> }
      | undefined;
    if (
      !geom ||
      geom.type !== "line" ||
      !Array.isArray(geom.points) ||
      geom.points.length < 2
    ) {
      continue;
    }
    const props = (obj.properties ?? {}) as Record<string, unknown>;
    const heightM = numberOr(props.height_m, 25);
    const speedMps = numberOr(props.speed_mps, 15.6);
    const loop = props.loop === undefined ? true : Boolean(props.loop);
    const lookAheadM = numberOr(props.look_ahead_m, 30);
    paths.push({
      id: String(obj.id ?? `flyby_${paths.length}`),
      points: geom.points.map(
        (p) => [Number(p[0]), Number(p[1])] as [number, number],
      ),
      heightM,
      speedMps,
      loop,
      lookAheadM,
    });
  }
  return paths;
}

function numberOr(value: unknown, fallback: number): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}
